/**
 * Optional extra server-side attributes for Meta events (e.g. client_pixel_fired).
 *
 * Kept separate from `commonFacebookEventsUtil` so the default dispatch stays
 * unchanged and this rollout can be reverted or extended per event name.
 *
 * `client_pixel_fired` is true only when the real FB SDK loaded (not stub-only)
 * and a facebook.com/tr (or connect.facebook.net) beacon was observed after track.
 *
 * The snippet always installs a stub `fbq` that only queues. The real SDK
 * (fbevents.js) attaches `callMethod`. `fbq.loaded` is set by the stub and
 * must not be used as an SDK-ready check.
 */

import {
  getFacebookCommonAttributes,
  isFacebookPixelSdkLoaded,
  postFacebookMetaServerEvent,
  prepareFacebookMetaEventDispatch,
  removeEmptyParams,
  waitForFacebookPixelSdk,
  type CommonFacebookEventsOptions,
  type FbqFn,
} from './facebookCommonAttributes';

const CLIENT_PIXEL_RETRY_MS = 1000;
/** Wait for facebook.com/tr (or SDK script) network activity after track. */
const CLIENT_PIXEL_BEACON_CONFIRM_MS = 500;

const FACEBOOK_TRACKING_URL_PATTERN = /facebook\.com\/tr|connect\.facebook\.net/i;

function countFacebookTrackingResources(): number {
  if (typeof performance === 'undefined') return 0;
  return performance
    .getEntriesByType('resource')
    .filter((entry) => FACEBOOK_TRACKING_URL_PATTERN.test(entry.name)).length;
}

function waitForNewFacebookTrackingResource(
  baselineCount: number,
  timeoutMs: number,
): Promise<boolean> {
  if (typeof performance === 'undefined') return Promise.resolve(false);

  return new Promise((resolve) => {
    let settled = false;
    let observer: PerformanceObserver | undefined;
    // eslint-disable-next-line prefer-const
    let intervalId: ReturnType<typeof setInterval> | undefined;
    // eslint-disable-next-line prefer-const
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const finish = (value: boolean) => {
      if (settled) return;
      settled = true;
      observer?.disconnect();
      if (intervalId != null) clearInterval(intervalId);
      if (timeoutId != null) clearTimeout(timeoutId);
      resolve(value);
    };

    const hasNewBeacon = () =>
      performance
        .getEntriesByType('resource')
        .filter((entry) => FACEBOOK_TRACKING_URL_PATTERN.test(entry.name)).length >
      baselineCount;

    if (hasNewBeacon()) {
      finish(true);
      return;
    }

    try {
      observer = new PerformanceObserver(() => {
        if (hasNewBeacon()) finish(true);
      });
      observer.observe({ type: 'resource', buffered: true });
    } catch {
      // PerformanceObserver unavailable — fall back to polling only.
    }

    intervalId = setInterval(() => {
      if (hasNewBeacon()) finish(true);
    }, 50);

    timeoutId = setTimeout(() => finish(false), timeoutMs);
  });
}

function trackFacebookPixel(
  eventName: string,
  eventAttributes: Record<string, unknown>,
  eventId: string,
): boolean {
  const fbq = window.fbq as FbqFn | undefined;
  if (typeof fbq !== 'function') return false;

  const payload = removeEmptyParams({
    ...getFacebookCommonAttributes(),
    ...eventAttributes,
  });

  try {
    fbq('track', eventName, payload, { eventID: eventId });
    const inspect = (stage: string) => {
      try {
        const fbq = window.fbq as FbqFn & {
          callMethod?: unknown;
          queue?: unknown[];
        };
        const queue = Array.isArray(fbq?.queue) ? [...fbq.queue] : [];
        console.log(`[Facebook: ${eventName}: ${stage}] -`, { 
          isSdkLoaded: typeof fbq?.callMethod === 'function', 
          queueLength: queue.length, 
          queue
        });
      } catch (error) {
        console.warn(`[Facebook: ${eventName}: ${stage}] inspection failed`, error);
      }
    };
    inspect('At Track Call');
    setTimeout(() => {
      inspect('After 1s (Flush Check)');
    }, 1000);
    console.log('client event triggered...', eventName, payload, { eventID: eventId });
    return true;
  } catch (error) {
    console.error(`Facebook Pixel track failed for '${eventName}':`, error);
    return false;
  }
}

/** Add event names here to opt into server attribution extras. */
export const FACEBOOK_EVENTS_WITH_SERVER_ATTRIBUTION = new Set<string>([
  'ViewContent',
]);

export function isFacebookServerAttributionEnabled(eventName: string): boolean {
  return FACEBOOK_EVENTS_WITH_SERVER_ATTRIBUTION.has(eventName);
}

async function awaitFacebookClientPixel(
  eventName: string,
  eventAttributes: Record<string, unknown>,
  eventId: string,
): Promise<boolean> {
  let trackInvoked = false;
  let beaconBaseline: number | null = null;

  const invokeTrackOnce = (): boolean => {
    if (trackInvoked) return true;

    const fbq = window.fbq as FbqFn | undefined;
    if (typeof fbq !== 'function') return false;

    beaconBaseline = countFacebookTrackingResources();

    if (!trackFacebookPixel(eventName, eventAttributes, eventId)) {
      beaconBaseline = null;
      return false;
    }

    trackInvoked = true;
    return true;
  };

  /** Confirm SDK loaded + outbound beacon after the single track call. Never re-tracks. */
  const confirmDelivery = async (): Promise<boolean> => {
    if (!trackInvoked || beaconBaseline == null) return false;
    if (!isFacebookPixelSdkLoaded()) return false;
    return waitForNewFacebookTrackingResource(beaconBaseline, CLIENT_PIXEL_BEACON_CONFIRM_MS);
  };

  if (invokeTrackOnce()) {
    if (await confirmDelivery()) return true;
  }

  return new Promise((resolve) => {
    setTimeout(async () => {
      if (!trackInvoked) {
        invokeTrackOnce();
      }

      const confirmed = await confirmDelivery();
      if (!confirmed) {
        console.warn(`Facebook Pixel delivery unconfirmed for '${eventName}'.`);
      }
      resolve(confirmed);
    }, CLIENT_PIXEL_RETRY_MS);
  });
}

async function buildFacebookServerAttributionExtras(
  eventName: string,
  eventAttributes: Record<string, unknown>,
  eventId: string,
): Promise<Record<string, unknown>> {
  if (!isFacebookServerAttributionEnabled(eventName)) {
    return {};
  }

  const clientPixelFired = await awaitFacebookClientPixel(eventName, eventAttributes, eventId);
  return {
    client_pixel_fired: clientPixelFired,
    variation: 'React',
    event_id: eventId,
  };
}

/**
 * Meta dispatch with optional server attribution extras.
 * Use instead of `commonFacebookEventsUtil` for opted-in events (ViewContent today).
 */
export function commonFacebookEventsUtilWithServerAttribution(
  eventName: string,
  eventAttributes: Record<string, unknown> = {},
  options?: CommonFacebookEventsOptions,
): void {
  const prepared = prepareFacebookMetaEventDispatch(eventName, eventAttributes);
  if (!prepared) return;

  void dispatchFacebookMetaEventWithServerAttribution(
    eventName,
    prepared.attributes,
    prepared.eventId,
    options,
  );
}

async function dispatchFacebookMetaEventWithServerAttribution(
  eventName: string,
  attributes: Record<string, unknown>,
  eventId: string,
  options?: CommonFacebookEventsOptions,
): Promise<void> {
  const serverExtras = await buildFacebookServerAttributionExtras(eventName, attributes, eventId);

  if (options?.skipServer) return;

  postFacebookMetaServerEvent(eventName, {
    ...attributes,
    ...serverExtras,
  });
}

/**
 * FindLocation-only: fire client pixel + server POST together, only after the
 * real Meta SDK tracks. Stub `fbq` (no `callMethod`) is not a pixel fire.
 */
export function commonFacebookEventsUtilIfMetaPixelFires(
  eventName: string,
  eventAttributes: Record<string, unknown> = {},
): void {
  const prepared = prepareFacebookMetaEventDispatch(eventName, eventAttributes);
  if (!prepared) return;

  void dispatchFacebookMetaEventIfPixelSdkTracks(
    eventName,
    prepared.attributes,
    prepared.eventId,
  );
}

async function dispatchFacebookMetaEventIfPixelSdkTracks(
  eventName: string,
  attributes: Record<string, unknown>,
  eventId: string,
): Promise<void> {
  const sdkReady = await waitForFacebookPixelSdk(CLIENT_PIXEL_RETRY_MS);
  if (!sdkReady) {
    console.warn(
      `Facebook Pixel SDK not loaded. Skipping client and server for '${eventName}'.`,
    );
    return;
  }

  // Re-check at track time so a stub-only fbq cannot queue-and-count as fired.
  if (!isFacebookPixelSdkLoaded()) {
    console.warn(
      `Facebook Pixel SDK unavailable at track. Skipping client and server for '${eventName}'.`,
    );
    return;
  }

  if (!trackFacebookPixel(eventName, attributes, eventId)) {
    console.warn(
      `Facebook Pixel track did not run. Skipping server event for '${eventName}'.`,
    );
    return;
  }

  postFacebookMetaServerEvent(eventName, {
    ...attributes,
    event_id: eventId,
  });
}
